/**
 * Pure sync/diff helpers for the Todo undo/redo flow.
 *
 * Extracted from `useTodoUndoRedo` so they can be exercised in isolation —
 * the sync algorithm is the bug-prone part (4-way matrix of present/missing
 * across two snapshots) and a hook-level test would have to construct a full
 * React Query cache + cache-subscription harness to reach it.
 */

import api, { ApiError } from '@/lib/api';
import type { TodoListDetailResponse, TodoItemResponse, TodoSectionResponse } from '@/lib/api';

/**
 * Sync a target snapshot to the server by diffing against the current state.
 * Fires the minimal set of API calls to bring the server in line with the target.
 */
export async function syncToServer(
  listId: string,
  current: TodoListDetailResponse,
  target: TodoListDetailResponse,
): Promise<void> {
  const ops: Promise<unknown>[] = [];

  // --- Items ---
  const currentItemMap = new Map(current.items.map((i) => [i.itemId, i]));
  const targetItemMap = new Map(target.items.map((i) => [i.itemId, i]));

  // Items to delete (in current but not target)
  for (const itemId of currentItemMap.keys()) {
    if (!targetItemMap.has(itemId)) {
      ops.push(api.deleteTodoItem(listId, itemId));
    }
  }

  // Items "missing" in current but present in target — try restoring an
  // existing row first (e.g. one that disappeared from the active payload
  // because it was just marked completed), and only create a fresh row if the
  // server confirms it's truly gone (404). Without this, undoing a completion
  // would create a duplicate pending item alongside the still-completed row.
  for (const [itemId, targetItem] of targetItemMap) {
    if (!currentItemMap.has(itemId)) {
      ops.push(restoreOrCreateItem(listId, itemId, targetItem));
    }
  }

  // Items to update (in both but different)
  for (const [itemId, targetItem] of targetItemMap) {
    const currentItem = currentItemMap.get(itemId);
    if (!currentItem) continue;
    const diff = diffItem(currentItem, targetItem);
    if (diff) {
      ops.push(api.updateTodoItem(listId, itemId, diff));
    }
  }

  // --- Sections ---
  const currentSectionMap = new Map(current.sections.map((s) => [s.sectionId, s]));
  const targetSectionMap = new Map(target.sections.map((s) => [s.sectionId, s]));

  // Sections to delete (in current but not target)
  for (const sectionId of currentSectionMap.keys()) {
    if (!targetSectionMap.has(sectionId)) {
      ops.push(api.deleteTodoSection(listId, sectionId));
    }
  }

  // Sections to create (in target but not current)
  for (const [, targetSection] of targetSectionMap) {
    if (!currentSectionMap.has(targetSection.sectionId)) {
      ops.push(
        api.createTodoSection(listId, {
          name: targetSection.name,
          description: targetSection.description ?? undefined,
          parentSectionId: targetSection.parentSectionId ?? undefined,
          sectionType: targetSection.sectionType,
        }),
      );
    }
  }

  // Sections to update (in both but different)
  for (const [sectionId, targetSection] of targetSectionMap) {
    const currentSection = currentSectionMap.get(sectionId);
    if (!currentSection) continue;
    const diff = diffSection(currentSection, targetSection);
    if (diff) {
      ops.push(api.updateTodoSection(listId, sectionId, diff));
    }
  }

  // Execute all operations in parallel, swallow individual errors
  await Promise.allSettled(ops);

  // --- Reorder items per section ---
  const targetSectionIds = new Set<string | null>();
  for (const item of target.items) {
    targetSectionIds.add(item.sectionId);
  }
  for (const sectionId of targetSectionIds) {
    const targetOrder = target.items
      .filter((i) => i.sectionId === sectionId)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((i) => i.itemId);
    const currentOrder = current.items
      .filter((i) => i.sectionId === sectionId)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((i) => i.itemId);
    if (JSON.stringify(targetOrder) !== JSON.stringify(currentOrder) && targetOrder.length > 0) {
      await api.reorderTodoItems(listId, sectionId, targetOrder).catch(() => {});
    }
  }

  // --- Reorder sections ---
  const targetSectionOrder = target.sections
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((s) => s.sectionId);
  const currentSectionOrder = current.sections
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((s) => s.sectionId);
  if (
    JSON.stringify(targetSectionOrder) !== JSON.stringify(currentSectionOrder) &&
    targetSectionOrder.length > 0
  ) {
    await api.reorderTodoSections(listId, targetSectionOrder).catch(() => {});
  }
}

/**
 * Restore an existing item via PATCH; fall back to CREATE only if the server
 * confirms the item is truly gone. Split out so the fallback branch is easy
 * to assert in tests.
 */
export async function restoreOrCreateItem(
  listId: string,
  itemId: string,
  targetItem: TodoItemResponse,
): Promise<void> {
  try {
    await api.updateTodoItem(listId, itemId, {
      title: targetItem.title,
      description: targetItem.description ?? undefined,
      status: targetItem.status,
      sectionId: targetItem.sectionId,
    });
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      await api.createTodoItem(listId, {
        title: targetItem.title,
        description: targetItem.description ?? undefined,
        sectionId: targetItem.sectionId ?? undefined,
      });
      return;
    }
    throw err;
  }
}

export function diffItem(
  current: TodoItemResponse,
  target: TodoItemResponse,
): Record<string, unknown> | null {
  const changes: Record<string, unknown> = {};
  if (current.title !== target.title) changes.title = target.title;
  if (current.description !== target.description) changes.description = target.description;
  if (current.status !== target.status) changes.status = target.status;
  if (current.sectionId !== target.sectionId) changes.sectionId = target.sectionId;
  return Object.keys(changes).length > 0 ? changes : null;
}

export function diffSection(
  current: TodoSectionResponse,
  target: TodoSectionResponse,
): Record<string, unknown> | null {
  const changes: Record<string, unknown> = {};
  if (current.name !== target.name) changes.name = target.name;
  if (current.description !== target.description) changes.description = target.description;
  if (current.sectionType !== target.sectionType) changes.sectionType = target.sectionType;
  if (current.parentSectionId !== target.parentSectionId)
    changes.parentSectionId = target.parentSectionId;
  return Object.keys(changes).length > 0 ? changes : null;
}
